import {cloneDeep, concat, filter, find, isEqual, map, remove, uniqWith,} from 'lodash';


import {withPrefix} from 'gatsby-link';

import {getImage} from 'gatsby-plugin-image';

const imageProperties = {
  'alternative-en': 'alternativeTextEN',
  'alternative-fr': 'alternativeText',
  'caption-en': 'captionEN',
  'caption-fr': 'caption',
  'creator-en': 'creator',
  'creator-fr': 'creator',
  'legend-label-en': 'Cover image caption: ',
  'legend-label-fr': 'Légende de l\'image de couverture : ',
  'legend-label-credits-en': 'credits: ',
  'legend-label-credits-fr': 'crédits : ',
};

export const altNN = (alt) => {
  return alt || '';
};

export const getImageAndAlt = (obj, websiteLanguage) => {
  if (obj && obj.imageFile) {
    const ret = {
      image: getImage(obj.imageFile),
      alt: obj[imageProperties[`alternative-${websiteLanguage}`]],
    };
    const lr = [];
    const captionProp = imageProperties[`caption-${websiteLanguage}`];
    if (obj[captionProp]) {
      const legendLabel = imageProperties[`legend-label-${websiteLanguage}`];
      lr.push(`${legendLabel} ${obj[captionProp]}`);
    }
    const creatorProp = imageProperties[`creator-${websiteLanguage}`];
    if (obj[creatorProp]) {
      const creatorLabel =
                imageProperties[`legend-label-credits-${websiteLanguage}`];
      lr.push(`(${creatorLabel} ${obj[creatorProp]})`);
    }
    if (lr.length > 0) {
      ret.legend = lr.join(' ');
    }
    return ret;
  }
  return null;
};

export const getLatestOrOngoingPosition = (array) => {
  array.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
  const lastaffiliation = array.find(position => position.end_date === null);
  if (lastaffiliation) {
    return lastaffiliation;
  }
  return array[0];
};

export const getLogoAndAlt = (obj, websiteLanguage) => {
  if (obj && obj.imageFile) {
    const prop =
            websiteLanguage === 'en' ? 'alternativeTextEN' : 'alternativeText';
    return {
      url: obj.imageFile.publicURL,
      alt: obj[prop],
    };
  }
  return null;
};

export const toJSON = (value) => {
  if (value) {
    if (typeof value === 'string') {
      return JSON.parse(value);
    }
    return JSON.parse(JSON.stringify(value));

  }
  return null;
};

const extractHTMLText = (html) => {
  let cpyhtml = html;
  if (cpyhtml) {
    cpyhtml = cpyhtml.replace(/<figure(.)*[^>]>(.*)<\/figure>/gi, '');
    cpyhtml = cpyhtml.replace(/(<([^>]+)>)/gi, '');

    if (cpyhtml.length > 350) {
      cpyhtml = cpyhtml.substring(0, 350);
      const lastIndexOfSpace = cpyhtml.lastIndexOf(' ');
      if (lastIndexOfSpace > 0) {
        cpyhtml = cpyhtml.substring(0, lastIndexOfSpace);
      }
    }

  }

  return cpyhtml;
};

const getFirstContentDescription = (content) => {
  if (content && Array.isArray(content)) {
    for (const ct of content) {
      const ctDesc = getFirstContentDescription(ct);
      if (ctDesc) {
        return ctDesc;
      }
    }
  } else {
    if (content && content.__typename) {
      if (content.__typename === 'CmsPortalComponentTabTab') {
        for (const tb of content.TabPanel) {
          const ctDesc = extractHTMLText(tb.content);
          if (ctDesc) {
            return ctDesc;
          }
        }
      }
      if (content.__typename === 'CmsPortalComponentContentText') {
        const ctDesc = extractHTMLText(content.text);
        if (ctDesc) {
          return ctDesc;
        }
      }
    }
  }
  return null;
};

export const getMetaDescription = (page, seo) => {
  if (seo && seo.Meta) {
    for (const s of seo.Meta) {
      if (s.name === 'description' && s.content) {
        return s.content;
      }
    }
  }
  if (page) {
    if (page.heading && page.heading.trim() !== '') {
      return page.heading.trim();
    }

    return getFirstContentDescription(page.content);
  }
  return null;
};

const getPageSeo = (item) => {
  if (item) {
    if (item.page) {
      // internal page
      const page = item.page;
      return {
        title: page.title,
        locale: page.locale || page.evt_language || null,
        seo: page.seo,
        link: page.canonical_link,
      };
    } else if (item.cmsPortalEventGuide) {
      const guide = item.cmsPortalEventGuide;
      return {
        title: guide.title,
        locale: guide.locale || null,
        seo: guide.seo,
        link: guide.canonical_link,
      };
    } else if (item.cmsPortalNewsGuide) {
      const guide = item.cmsPortalNewsGuide;
      return {
        title: guide.title,
        locale: guide.locale || null,
        seo: guide.seo,
        link: guide.canonical_link,
      };
    }
  }
  return null;
};

const getWebsiteSeo = (website) => {
  if (website) {
    return {
      name: website.name,
      locale: website.locale,
      seo: website.seo,
      description: website.description
    };
  }
  return null;
};

const getGeneralSeo = (settings) => {
  if (settings) {
    return {
      seo: settings.seo
    };
  }
  return null;
};

const getWebsiteLogo = (website, gs, language) => {
  if (website && website.logo) {
    return getLogoAndAlt(website.logo, language);
  } else if (gs && gs.logo) {
    return getLogoAndAlt(gs.logo, language);
  }
  return null;
};

const getWebsiteFooter = (website, gs, siteURL) => {
  const footer = website?.website_footer || gs?.footer || null;
  if (footer) {
    const {logo, ...otherProperties} = footer;
    if (logo) {
      return {
        ...otherProperties,
        logo: {
          ...getLogoAndAlt(logo, website.locale),
          navigationPath: siteURL,
        },
      };
    }
  }
  return footer;
};

const getPartnerLogo = (website) => {
  if (website && website.partner_logo && website.partner_logo.logo) {
    return {
      linkTo: website.partner_logo.URL,
      ...getLogoAndAlt(website.partner_logo.logo, website.locale),
    };
  }
  return null;
};

const getCanonicalLink = (data) => {
  if (data?.page?.canonical_link) return data?.page?.canonical_link;
  if (data?.cmsPortalNewsGuide?.canonical_link) return data?.cmsPortalNewsGuide?.canonical_link;
  if (data?.cmsPortalEventGuide?.canonical_link) return data?.cmsPortalEventGuide?.canonical_link;
  return null;
};

export const getLayoutInformations = (data, siteURL) => {
  if (!data) {
    throw new Error('invalid arguments. The data object is required');
  }
  const locale = data.website?.locale || 'fr';
  const generalSettings = data[`${locale}GeneralSettings`];
  return {
    locale: data?.website?.locale || null,
    localizations: data?.website?.localizations || null,
    name: data?.website?.name || null,
    logo: {
      ...getWebsiteLogo(data?.website, generalSettings, locale),
      navigationPath: siteURL,
    },
    logoPartner: getPartnerLogo(data?.website),
    menu: toJSON(data?.website?.menu)?.menu || [],
    corporateMenu: toJSON(data?.website?.corporate_menu?.menu) || [],
    communityMenu: toJSON(data?.website?.community_menu?.menu),
    footer: getWebsiteFooter(data?.website, generalSettings, siteURL),
    generalSeo: getGeneralSeo(generalSettings),
    websiteSeo: getWebsiteSeo(data?.website),
    pageSeo: getPageSeo(data),
    canonical_link: getCanonicalLink(data),
    metaDescription: getMetaDescription(data?.page, data?.page?.seo),
  };
};

export const getSEOInformations = (data) => {
  if (!data) {
    throw new Error('invalid arguments. The data object is required');
  }
  return {
    pageSeo: getPageSeo(data),
    canonical_link: getCanonicalLink(data),
    metaDescription: getMetaDescription(data?.page, data?.page?.seo),
  };
};

export const mapSectionIntoMain = (content) => {
  return content?.map((elem) => {
    if (elem?.__typename === 'CmsPortalComponentSectionSectionContainer') {
      if (elem.section?.content?.length > 0) {
        elem = elem.section?.content[0];
      }
      elem.__typename = 'CmsPortalComponentSectionMain';
    }
    return elem;
  });
};

export const removeUselessWidgets = (content) => {
  content?.forEach((item) => {
    if (item.__typename === 'CmsPortalComponentSectionMain') {
      remove(item?.content, (item) => {
        if (item.widget?.content.length > 0) {
          return (
            item.widget?.content[0]?.__typename ===
                        'CmsPortalComponentWidgetTwitter' ||
                        item.widget?.content[0]?.__typename ===
                        'CmsPortalComponentWidgetYoutubeStream' ||
                        item.widget?.content[0]?.__typename ===
                        'CmsPortalComponentWidgetEmbededContent'
          );
        }
        return false;
      });
    }
  });
  return content;
};

export const getInternalPageVideoHeader = (header) => {
  if (header && header.length > 0) {
    const hd = header[0];
    if (hd && hd.__typename === 'CmsPortalComponentContentPageVideoHeader') {
      return hd;
    }
  }
  return null;
};


export const getInternalPageCover = (header) => {
  if (header && header.length > 0) {
    const hd = header[0];
    if (hd) {
      return hd.cover_media || null;
    }
  }
  return null;
};

export const getInternalPageCarrousel = (header) => {
  if (header && header.length > 0) {
    const hd = header[0];
    if (hd && hd.carousel_item && hd.carousel_item.length > 0) {
      return hd;
    }
  }
  return null;
};

export const getDate = (date, delimiter, options, locale) => {
  let parsedDate = date;
  if (!delimiter) {
    delimiter = '.';
  }
  const standardOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'CET'
  };
  if (date) {
    parsedDate =
    new Date(date).toLocaleString(locale || 'fr-FR', {
      day: options?.day ? options.day : standardOptions.day,
      timeZone: 'CET'
    }) +
    delimiter +
    new Date(date).toLocaleString(locale || 'fr-FR', {
      month: options?.month ? options.month : standardOptions.month,
      timeZone: 'CET'
    }) +
    delimiter +
    new Date(date).toLocaleString(locale || 'fr-FR', {
      year: options?.year ? options.year : standardOptions.year,
      timeZone: 'CET'
    });
  }
  return parsedDate;
};

export const getHour = (date) => {
  let parsedDate = date;
  if (date) {
    parsedDate = new Date(date).toLocaleTimeString('fr-FR', {
      hour: 'numeric',
      minute: 'numeric',
      timeZone: 'CET'
    });
  }
  return parsedDate;
};

export const getHeader = (
  promotedEvents,
  headerItems,
  siteURL,
  limit,
  news
) => {
  limit = limit ? limit : 20;
  let counter = 0;
  const filterInfo = (toFilter, counter, news) => {
    const res = [];
    toFilter?.forEach((item) => {
      if (counter < limit) {
        const obj = {
          newWindow: true,
          title: item.title,
          heading: item.heading,
          id: item.id,
          media: item.cover,
        };
        if (news) {
          obj.slug = `${siteURL}/news/${item.slug}`;
          obj.news_item = item;
        } else {
          obj.slug = `${siteURL}/events/${item.slug}`;
          obj.event = item;
        }
        res.push(obj);
        counter++;
      }
    });
    return res;
  };
  const filteredPromotedEvents = filterInfo(promotedEvents, counter, news);
  counter = counter + filteredPromotedEvents.length;
  const filteredHeaderEvents = filterInfo(headerItems, counter, news);
  return uniqWith(
    concat(filteredPromotedEvents, filteredHeaderEvents),
    isEqual
  );
};

export const getFilteredGuide = (list, othList) => {
  const arr = cloneDeep(list);
  remove(arr, (item) => {
    if (find(othList, (headerItem) => item.id === headerItem.id)) return true;
  });
  return arr;
};

export const getSiteURL = (url) => {
  if (url && !['/', ''].includes(url) && !url.endsWith('/')) {
    return `${url}/`;
  }
  return url;
};

export const handleLanguageSelection = (siteURL, language) => {
  if (!language) {
    return '';
  }
  siteURL = withPrefix(siteURL);

  if (siteURL.endsWith('/en') || siteURL.endsWith('/fr')) {
    return siteURL.slice(0, -2) + language.locale;
  }
  if (siteURL.endsWith('/en/') || siteURL.endsWith('/fr/')) {
    return siteURL.slice(0, -3) + language.locale;
  }
  if (siteURL.endsWith('/')) {
    return `${siteURL}${language.locale}`;
  }
  return `${siteURL}/${language.locale}`;


};

export const displayLanguages = (lang) => {
  let display;

  switch (lang?.locale) {
  case 'fr':
    display = 'Français';
    break;
  case 'en':
    display = 'English';
    break;
  default:
    display = lang;
  }

  return display;
};

export const displayAriaLabelLanguages = (lang) => {
  let display;

  switch (lang?.locale) {
  case 'fr':
    display = 'Version française du site internet';
    break;
  case 'en':
    display = 'English version of the website';
    break;
  default:
    display = lang;
  }

  return display;
};

export const getFilteredAlgoliaResults = (hits) => {
  const filtered = [];
  if (hits?.length > 0) {
    filtered.push({
      type: 'internalpage',
      hits: filter(hits, (item) => item.type === 'internalpage'),
    });
    filtered.push({
      type: 'news',
      hits: filter(hits, (item) => item.type === 'news'),
    });
    filtered.push({
      type: 'event',
      hits: filter(hits, (item) => item.type === 'event'),
    });
    filtered.push({
      type: 'exedproduct',
      hits: filter(hits, (item) => item.type === 'exedproduct'),
    });
  }
  return filtered;
};

export const getUrlParameter = (parameter) => {
  let p = null;
  if (parameter) {
    if (typeof window !== 'undefined') {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      p = urlParams.get(parameter);
    }
  }
  return p;
};

export const formatFacet = (facets) => {
  const formatted = map(facets, (item) => `type:${item.type}`);
  return formatted;
};

export const getDisplayLang = (itemLanguage, locale) => {
  let displayLang = '';
  if (locale && itemLanguage) {
    if (itemLanguage.toLowerCase() !== locale.toLowerCase()) {
      displayLang = itemLanguage.toLowerCase();
    }
  }
  return displayLang;
};
